import React, { useContext, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import Pane from 'components/Common/Pane'
import LETVDestinations from './LETVDestinations'
import { fetchListBanners } from 'actions/ContentActions'
import { BANNER_LOCATION_LE_TV_COMPETITION, BANNER_LOCATION_LE_TV_PAGE } from 'constants/bannerLocations'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import HotelOfferCarousel from './HotelOfferCarousel'
import HostCarousel from './HostCarousel'
import ImageHero from 'components/Common/ImageHero'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import { useAppDispatch } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import Error404Page from 'components/Pages/Error404Page'
import { OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import ResponsiveImage from 'components/Common/ResponsiveImage/ResponsiveImage'
import noop from 'lib/noop'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useLETVEpisodes } from 'content/api'
import PaletteProvider from 'contexts/PaletteProvider'
import EpisodeTabs from '../LETVEpisodePage/EpisodeTabs'
import { BeInspiredBanner } from './BeInspiredBanner/BeInspiredBanner'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
interface Props {
  teaserBanners: Array<App.ListBanner>;
  competitionBanners: Array<App.ListBanner>;
}

const StyledImageHero = styled(ImageHero)`
  max-height: ${rem(340)};
  height: ${rem(320)};
  position: relative;
`

const ImageHeroPane = styled(Group)`
  padding: ${rem(40)} ${rem(20)};
  z-index: 1;

  ${mediaQueryUp.sm} {
    padding-bottom: ${rem(60)};
  }
`

const EpisodeIntroWrapper = styled(Pane)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.sm} {
    padding: 0;
  }
`

const Section = styled.div`
  padding-top: ${rem(32)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.sm} {
    padding-top: ${rem(40)};
    padding-bottom: ${rem(40)};
  }
`

const Divider = styled.div`
  display: none;

  ${mediaQueryUp.sm} {
    display: block;
    margin: 0 ${rem(8)};
  }
`

function LETVLandingPage({ teaserBanners, competitionBanners }: Props) {
  const dispatch = useAppDispatch()
  const videoRef = useRef<HTMLImageElement>(null)
  const { data: leTVConfig, isLoading } = useLETVEpisodes()

  const { currentRegionCode } = useContext(GeoContext)

  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_LE_TV_PAGE))
    dispatch(fetchListBanners(BANNER_LOCATION_LE_TV_COMPETITION))
  }, [dispatch])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (currentRegionCode !== 'AU' || !leTVConfig?.leTVEpisodes?.length) {
    return <Error404Page invalidRegion />
  }

  return (
    <Pane>
      <StyledImageHero id="o6uxys94abqpwh4chc4" fit="center">
        <PaletteProvider type="inverse">
          <ImageHeroPane direction="vertical" horizontalAlign="center" gap={16}>
            <Group direction="vertical" gap={32}>
              <Group direction="vertical" gap={8}>
                <div>
                  <Heading variant="heading1" align="center">
                    Luxury Escapes:
                  </Heading>
                  <Heading variant="heading1" align="center">
                    The <i>World's Best</i> Holidays
                  </Heading>
                </div>
              </Group>
              <Group horizontalAlign="center" direction="vertical" smDirection="horizontal" smVerticalAlign="center" gap={8} smGap={0}>
                <BodyText variant="medium" as="span" align="center">Tuesday 8:30pm AEST</BodyText>
                <Divider>|</Divider>
                <Group horizontalAlign="center" direction="horizontal" verticalAlign="center" gap={12}>
                  <BodyText variant="medium" as="span" align="center">Watch or stream on</BodyText>
                  <svg height="12" viewBox="0 0 118 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5207 5.80691L16.1913 0.794857H0.5V24.1626H6.17815V15.7546H13.1885L14.8592 10.7425H6.16723V5.80691H14.5207ZM29.3384 0.50003C24.4902 0.489111 20.1005 3.41554 18.2442 7.89254C16.377 12.3695 17.4034 17.5345 20.8321 20.9741C24.2609 24.4138 29.4149 25.4402 33.9028 23.5948C38.3907 21.7385 41.3171 17.3707 41.3281 12.5115C41.3171 5.89427 35.9666 0.521869 29.3384 0.50003ZM29.3384 18.7902C26.7942 18.8011 24.4902 17.2615 23.5074 14.9138C22.5247 12.5661 23.0706 9.84714 24.8724 8.04542C26.6741 6.2437 29.3821 5.70864 31.7407 6.68048C34.0884 7.66323 35.6281 9.95633 35.6172 12.5115C35.6172 15.9839 32.7999 18.7902 29.3384 18.7902ZM61.147 0.805777H54.8027L50.828 7.18277L46.8315 0.805777H40.4873L47.6614 12.2057L40.1488 24.1626H46.493L50.839 17.2833L55.1412 24.1626H61.4855L54.0056 12.2057L61.147 0.805777ZM61.6493 5.80691H68.5613V24.1626H74.2395V5.80691H80.2452V0.794857H64.7832L61.6493 5.80691ZM97.3561 5.80691L99.0268 0.794857H83.3354V24.1626H97.3561L99.0268 19.1506H89.0136V14.6845H96.0239L97.6946 9.67242H89.0136V5.80691H97.3561ZM107.839 19.1506V0.794857H102.161V24.1626H116.181L117.863 19.1506H107.839Z" fill="white" />
                  </svg>
                  <ResponsiveImage sizes="24px" id="nfj12z2f1i8akoqx2sb" />
                </Group>
                <Divider>|</Divider>
                <Group horizontalAlign="center" direction="horizontal" verticalAlign="center" gap={12}>
                  <BodyText variant="medium" as="span" align="center">Encore screening</BodyText>
                  <svg width="20" height="20" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="10 logo 1" clipPath="url(#clip0_4679_13190)">
                      <g id="Clip path group">
                        <mask id="mask0_4679_13190" maskUnits="userSpaceOnUse" x="1" y="0" width="24" height="25">
                          <g id="clip0_3561_508530">
                            <path id="Vector" d="M24.4263 0.913818H1.25385V24.0862H24.4263V0.913818Z" fill="white" />
                          </g>
                        </mask>
                        <g mask="url(#mask0_4679_13190)">
                          <g id="Group">
                            <path id="Vector_2" d="M15.2275 10.4483C14.8239 10.4483 14.4293 10.568 14.0938 10.7922C13.7582 11.0165 13.4967 11.3352 13.3422 11.7081C13.1877 12.0809 13.1473 12.4912 13.2261 12.8871C13.3049 13.283 13.4993 13.6466 13.7847 13.9319C14.0701 14.2173 14.4337 14.4116 14.8296 14.4903C15.2255 14.569 15.6358 14.5285 16.0086 14.374C16.3815 14.2195 16.7002 13.9579 16.9244 13.6223C17.1485 13.2867 17.2681 12.8921 17.2681 12.4885C17.2681 12.2205 17.2153 11.9552 17.1127 11.7077C17.0102 11.4602 16.8599 11.2352 16.6704 11.0457C16.4809 10.8563 16.2559 10.706 16.0084 10.6035C15.7608 10.501 15.4954 10.4483 15.2275 10.4483ZM12.833 0.909363C6.43793 0.909363 1.25385 6.09345 1.25385 12.4885C1.25385 18.8836 6.43793 24.0677 12.833 24.0677C19.228 24.0677 24.4121 18.8829 24.4121 12.4885C24.4121 6.09419 19.2273 0.909363 12.833 0.909363ZM4.99746 9.3105C5.53843 9.30992 6.05708 9.09467 6.43958 8.71213C6.82208 8.32956 7.03717 7.81087 7.03766 7.26989H10.4396C10.4396 7.98467 10.2989 8.69243 10.0253 9.35279C9.75167 10.0131 9.3507 10.6131 8.84526 11.1184C8.33979 11.6239 7.73971 12.0247 7.07931 12.2981C6.41891 12.5715 5.71111 12.7122 4.99634 12.712L4.99746 9.3105ZM15.2275 17.9307C14.2456 17.9313 13.282 17.6661 12.4386 17.1635C11.5952 16.6608 10.9036 15.9392 10.437 15.0754V17.7071H7.03655V12.956C8.48559 12.4393 9.68165 11.3871 10.3787 10.0157C10.8346 9.12198 11.5287 8.37175 12.3842 7.8478C13.2398 7.32386 14.2235 7.04662 15.2267 7.04671C16.6701 7.04671 18.0543 7.62008 19.0749 8.64069C20.0955 9.66132 20.6689 11.0455 20.6689 12.4888C20.6689 13.9322 20.0955 15.3165 19.0749 16.3371C18.0543 17.3577 16.6709 17.9307 15.2275 17.9307Z" fill="white" />
                          </g>
                        </g>
                      </g>
                    </g>
                    <defs>
                      <clipPath id="clip0_4679_13190">
                        <rect width="24" height="24" fill="white" transform="translate(0.840057 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                </Group>
              </Group>
            </Group>
            <BodyText variant="small">Brought to you by Citi Credit Cards</BodyText>
          </ImageHeroPane>
        </PaletteProvider>
      </StyledImageHero>
      <PaletteProvider type="inverse">
        <EpisodeIntroWrapper>
          <LayoutContainer size="sm">
            <EpisodeTabs leTVEpisodes={leTVConfig?.leTVEpisodes} />
          </LayoutContainer>
          <LayoutContainer>
            <BeInspiredBanner videoRef={videoRef} />
          </LayoutContainer>
        </EpisodeIntroWrapper>
      </PaletteProvider>
      <Section>
        <HostCarousel hosts={leTVConfig?.allHosts} />
      </Section>
      <Section>
        <OfferListEventsProvider onListEvent={noop}>
          <LETVDestinations />
        </OfferListEventsProvider>
      </Section>
      {competitionBanners && (
        <Section>
          <LayoutContainer size="lg">
            <BannerCarousel banners={competitionBanners} />
          </LayoutContainer>
        </Section>
      )}
      <Section>
        <Group direction="vertical" gap={20}>
          <LayoutContainer size="lg">
            <OfferCarouselHeader allPath="">
              Explore our <i>exclusive offers</i>
            </OfferCarouselHeader>
          </LayoutContainer>
          <HotelOfferCarousel />
        </Group>
      </Section>
      {teaserBanners && (
        <Section>
          <LayoutContainer size="lg">
            <BannerCarousel banners={teaserBanners} />
          </LayoutContainer>
        </Section>
      )}
      <Section>
        <LayoutContainer size="lg">
          <Group direction="vertical" gap={8}>
            <Heading variant="heading6">Terms & Conditions</Heading>
            <BodyText variant="small" colour="neutral-three">National Australia Bank Limited (ABN 12 004 044 937, AFSL and Australian Credit Licence 230686) ("NAB") is the credit provider and issuer of Citi branded financial and credit products. NAB has acquired the business relating to these products from Citigroup Pty Ltd (ABN 88 004 325 080, AFSL and Australian Credit Licence 238098) ("Citi") and has appointed Citi to provide transitional services. Our/us/we means NAB unless the context otherwise requires it. “Citi", "Citibank", "Citigroup", the Arc design and all similar trade marks and derivations thereof are used temporarily under licence by NAB from Citigroup Inc. and related group entities.</BodyText>
          </Group>
        </LayoutContainer>
      </Section>
    </Pane>
  )
}

const mapStateToProps = (state: App.State) => {
  return ({
    teaserBanners: state.content.listBanners?.leTVPage,
    competitionBanners: state.content.listBanners?.leTVCompetition,
  })
}

export default connect(mapStateToProps)(LETVLandingPage)
